// Format date
export const formatDate = (date, format = 'short') => {
  if (!date) return '-';
  
  const d = new Date(date);
  
  if (format === 'short') {
    return d.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  }
  
  if (format === 'long') {
    return d.toLocaleDateString('en-GB', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  }
  
  if (format === 'relative') {
    const now = new Date();
    const diff = now - d;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;
    if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
    if (days < 365) return `${Math.floor(days / 30)} months ago`;
    return `${Math.floor(days / 365)} years ago`;
  }
  
  return d.toLocaleDateString('en-GB');
};

// Format large numbers with K, M, B suffixes
export const formatNumber = (num, decimals = 1) => {
  if (!num || num === 0) return '0';
  
  const absNum = Math.abs(num);
  
  if (absNum >= 1e9) {
    return (num / 1e9).toFixed(decimals) + 'B';
  }
  if (absNum >= 1e6) {
    return (num / 1e6).toFixed(decimals) + 'M';
  }
  if (absNum >= 1e3) {
    return (num / 1e3).toFixed(decimals) + 'K';
  }
  
  return num.toLocaleString();
};

// Format as full number with commas
export const formatFullNumber = (num) => {
  if (!num || num === 0) return '0';
  return num.toLocaleString();
};

// Format currency
export const formatCurrency = (amount, currency = 'GBP') => {
  if (!amount || amount === 0) return '£0';
  
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(amount);
};

// Format percentage
export const formatPercent = (value, decimals = 1) => {
  if (!value || value === 0) return '0%';
  return `${value.toFixed(decimals)}%`;
};

// Format engagement rate
export const formatEngagementRate = (rate) => {
  if (!rate || rate === 0) return '0%';
  return `${rate.toFixed(2)}%`;
};

// Format watch hours - always show full numbers
export const formatWatchHours = (hours) => {
  if (!hours || hours === 0) return '0 hrs';
  return `${Math.round(hours).toLocaleString()} hrs`;
};

// Format duration (minutes to readable)
export const formatDuration = (minutes) => {
  if (!minutes || minutes === 0) return '0m';
  
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  
  if (hours > 0) {
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  }
  
  return `${mins}m`;
};

// Format date range
export const formatDateRange = (startDate, endDate) => {
  const start = formatDate(startDate, 'short');
  const end = formatDate(endDate, 'short');
  return `${start} - ${end}`;
};

// Truncate text
export const truncateText = (text, maxLength = 50) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
};

// Get platform color
export const getPlatformColor = (platform) => {
  if (!platform) return '#6B7280';
  
  const colors = {
    twitch: '#9146FF',
    youtube: '#FF0000',
    tiktok: '#000000',
    twitter: '#1DA1F2',
    instagram: '#E4405F',
    discord: '#5865F2',
    facebook: '#1877F2',
    kick: '#00E70A'
  };
  
  return colors[platform?.toLowerCase()] || '#6B7280';
};

// Get tier color
export const getTierColor = (tier) => {
  if (!tier) return '#6B7280';
  
  const colors = {
    platinum: '#E5E7EB',
    gold: '#FBBF24',
    silver: '#D1D5DB',
    bronze: '#A16207',
    standard: '#6B7280',
    tier1: '#6366F1',
    tier2: '#8B5CF6',
    tier3: '#EC4899'
  };
  
  return colors[tier?.toLowerCase()] || '#6B7280';
};

// Get status color
export const getStatusColor = (status) => {
  if (!status) return '#6B7280';

  const colors = {
    active: '#10B981',
    inactive: '#6B7280',
    pending: '#F59E0B',
    completed: '#3B82F6',
    paused: '#EF4444',
    draft: '#6B7280',
    approved: '#10B981',
    rejected: '#EF4444'
  };

  return colors[status?.toLowerCase()] || '#6B7280';
};

// Country name to ISO 3166-1 alpha-2 code mapping
const countryToCode = {
  // Common names and variations
  'uk': 'GB',
  'united kingdom': 'GB',
  'great britain': 'GB',
  'england': 'GB',
  'scotland': 'GB',
  'wales': 'GB',
  'northern ireland': 'GB',
  'britain': 'GB',
  'usa': 'US',
  'united states': 'US',
  'united states of america': 'US',
  'america': 'US',
  'us': 'US',
  'canada': 'CA',
  'australia': 'AU',
  'germany': 'DE',
  'france': 'FR',
  'spain': 'ES',
  'italy': 'IT',
  'netherlands': 'NL',
  'holland': 'NL',
  'belgium': 'BE',
  'sweden': 'SE',
  'norway': 'NO',
  'denmark': 'DK',
  'finland': 'FI',
  'ireland': 'IE',
  'portugal': 'PT',
  'austria': 'AT',
  'switzerland': 'CH',
  'poland': 'PL',
  'czech republic': 'CZ',
  'czechia': 'CZ',
  'hungary': 'HU',
  'romania': 'RO',
  'bulgaria': 'BG',
  'greece': 'GR',
  'turkey': 'TR',
  'russia': 'RU',
  'ukraine': 'UA',
  'japan': 'JP',
  'china': 'CN',
  'south korea': 'KR',
  'korea': 'KR',
  'india': 'IN',
  'indonesia': 'ID',
  'thailand': 'TH',
  'vietnam': 'VN',
  'philippines': 'PH',
  'malaysia': 'MY',
  'singapore': 'SG',
  'taiwan': 'TW',
  'hong kong': 'HK',
  'brazil': 'BR',
  'mexico': 'MX',
  'argentina': 'AR',
  'chile': 'CL',
  'colombia': 'CO',
  'peru': 'PE',
  'south africa': 'ZA',
  'egypt': 'EG',
  'nigeria': 'NG',
  'kenya': 'KE',
  'morocco': 'MA',
  'israel': 'IL',
  'saudi arabia': 'SA',
  'uae': 'AE',
  'united arab emirates': 'AE',
  'new zealand': 'NZ',
  'iceland': 'IS',
  'luxembourg': 'LU',
  'croatia': 'HR',
  'serbia': 'RS',
  'slovenia': 'SI',
  'slovakia': 'SK',
  'estonia': 'EE',
  'latvia': 'LV',
  'lithuania': 'LT',
  'malta': 'MT',
  'cyprus': 'CY'
};

// Convert country code to flag emoji using regional indicator symbols
const codeToFlag = (code) => {
  if (!code || code.length !== 2) return null;
  const upperCode = code.toUpperCase();
  // Convert each letter to its regional indicator symbol (A=🇦, B=🇧, etc.)
  return String.fromCodePoint(
    ...upperCode.split('').map(char => 0x1F1E6 + char.charCodeAt(0) - 65)
  );
};

// Get flag emoji for a country name or code
export const getCountryFlag = (country) => {
  if (!country) return '🌍';

  const normalized = country.trim().toLowerCase();

  // Check if it's already a 2-letter code
  if (normalized.length === 2) {
    const flag = codeToFlag(normalized);
    if (flag) return flag;
  }

  // Look up the country name
  const code = countryToCode[normalized];
  if (code) {
    return codeToFlag(code);
  }

  // Fallback to globe for unknown countries
  return '🌍';
};
